import type { ComponentType } from 'react';
import { Home } from 'lucide-react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { WebModule } from '@nw/web-platform';

/** A place in the OS: Homebase, or a module that takes the whole screen. */
export interface Space {
  id: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  path: string;
  category: string;
  hue: number;
  mode: 'light' | 'dark';
  description?: string;
  module?: WebModule;
}

export const HOME: Space = {
  id: 'home',
  label: 'Home',
  icon: Home,
  path: '/',
  category: 'Home',
  hue: 262,
  mode: 'light',
  description: 'Your instruments',
};

const TONE_HUE: Record<string, number> = {
  sage: 95,
  blue: 215,
  grey: 220,
  peach: 28,
  dark: 250,
};

export function spaceOf(m: WebModule): Space | null {
  if (!m.nav) return null;
  return {
    id: m.id,
    label: m.nav.label,
    icon: m.nav.icon,
    path: m.nav.path,
    category: m.nav.category ?? 'Modules',
    hue: m.space?.hue ?? TONE_HUE[m.nav.tone ?? 'grey'] ?? 220,
    mode: m.space?.mode ?? (m.nav.tone === 'dark' ? 'dark' : 'light'),
    description: m.nav.description,
    module: m,
  };
}

/** Every space in order: Homebase first, then modules by their order. */
export function allSpaces(modules: WebModule[]): Space[] {
  return [
    HOME,
    ...modules
      .filter((m) => m.nav)
      .sort((a, b) => a.nav!.order - b.nav!.order)
      .map((m) => spaceOf(m)!),
  ];
}

/** The spaces you swipe between: Homebase and your pinned modules (all of them until you pin). */
export function pagerSpaces(spaces: Space[], pinned: string[] | null | undefined): Space[] {
  if (!pinned?.length) return spaces;
  return spaces.filter((s) => s.id === 'home' || pinned.includes(s.id));
}

/** Which space owns a path. Shell pages (settings, people) belong to no space: null. */
export function spaceIdFor(pathname: string, modules: WebModule[]): string | null {
  if (pathname === '/' || pathname === '') return 'home';
  for (const m of modules) {
    if (m.windows?.(pathname)) return m.id;
    if (m.nav && (pathname === m.nav.path || pathname.startsWith(`${m.nav.path}/`))) return m.id;
  }
  return null;
}

interface SpaceMemory {
  workspaceId: string | null;
  /** Where you last were inside each space, so coming back lands there. */
  last: Record<string, string>;
  forWorkspace: (id: string) => void;
  remember: (spaceId: string, href: string) => void;
}

const safeStorage = createJSONStorage(() => {
  try {
    return window.localStorage;
  } catch {
    return undefined as unknown as Storage;
  }
});

export const useSpaceMemory = create<SpaceMemory>()(
  persist(
    (set) => ({
      workspaceId: null,
      last: {},
      forWorkspace: (workspaceId) => set((s) => (s.workspaceId === workspaceId ? s : { workspaceId, last: {} })),
      remember: (spaceId, href) => set((s) => (s.last[spaceId] === href ? s : { last: { ...s.last, [spaceId]: href } })),
    }),
    { name: 'nw:spaces', storage: safeStorage, version: 1 },
  ),
);
